import React, { useState } from "react";
import { MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useChat } from "../../contexts/ChatContext";
import toast from "react-hot-toast";
import { localizedField } from "../../utils/localizedField";
import { useAppLanguage } from "../../utils/appLanguage";

const SIDEBAR_COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
};

interface HealJaiTalkButtonProps {
  category: any;
  label?: string; // Override display label (e.g. "Astria Talk")
}

const HealJaiTalkButton: React.FC<HealJaiTalkButtonProps> = ({
  category,
  label,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setCurrentChat, startNewChat } = useChat();

  const [startingChatId, setStartingChatId] = useState<string | null>(null);
  const isLoading = startingChatId === category.id;
  const appLanguage = useAppLanguage();

  const handleNewChat = async () => {
    try {
      setStartingChatId(category.id);

      const newChat = await startNewChat(category.id);
      setCurrentChat(newChat);
      navigate(`/sanctuary?chat=${newChat.id}`);
    } catch (error) {
      console.error("Failed to start new chat:", error);
      toast.error(
        t("sidebar.failedToStartChat") ||
          t("toast.healjaiTalk.chatStartFailed"),
      );
    } finally {
      setStartingChatId(null);
    }
  };

  return (
    <motion.button
      key={category.id}
      type="button"
      onClick={handleNewChat}
      disabled={isLoading}
      whileHover={{ scale: 1.04, y: -2 }}
      whileTap={{ scale: 0.94 }}
      animate={
        isLoading
          ? {
              scale: [1, 1.06, 1],
              boxShadow: [
                "0 14px 32px rgba(0,0,0,0.25)",
                "0 0 0 10px rgba(109,76,146,0.16)",
                "0 14px 32px rgba(0,0,0,0.25)",
              ],
            }
          : {
              y: [0, -3, 0],
            }
      }
      transition={
        isLoading
          ? {
              duration: 0.9,
              repeat: Infinity,
              ease: "easeInOut",
            }
          : {
              y: {
                duration: 2.2,
                repeat: Infinity,
                ease: "easeInOut",
              },
            }
      }
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full px-3 py-3 text-white shadow-[0_14px_32px_rgba(0,0,0,0.25)] transition active:scale-[0.98] disabled:opacity-90"
      style={{
        background: `linear-gradient(135deg, ${SIDEBAR_COLORS.amethyst}, ${SIDEBAR_COLORS.darkAmethyst})`,
      }}
      aria-label={t("sanctuary.healjaiTalk.buttonLabel") || "HealJai Talk"}
    >
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0, rotate: -90, scale: 0.8 }}
            animate={{ opacity: 1, rotate: 360, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              ease: "linear",
            }}
            className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white"
          />
        ) : (
          <motion.div
            key="icon"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <MessageCircle className="w-4 h-4" />
          </motion.div>
        )}
      </AnimatePresence>

      <motion.span
        animate={isLoading ? { opacity: [1, 0.7, 1] } : { opacity: 1 }}
        transition={
          isLoading
            ? { duration: 0.9, repeat: Infinity, ease: "easeInOut" }
            : { duration: 0.2 }
        }
        className="text-sm font-semibold"
      >
        {isLoading
          ? t("sanctuary.healjaiTalk.opening") || "Opening..."
          : label
            ? label
            : localizedField(category, "name", appLanguage)}
      </motion.span>
    </motion.button>
  );
};

export default HealJaiTalkButton;
